import type { FeedEvent } from "./feedTypes";

const ARTIFACT_LABELS: Record<string, string> = {
  plan_cheat_sheet: "cheat sheet",
  suitability_letter: "suitability letter",
  recommendations_letter: "recommendations letter",
  starter_session_deck: "starter session deck",
  starter_session_brief: "starter session brief",
  short_letter: "letter",
  plan_fees_summary: "plan fees summary",
  loa: "LOA",
};

export function formatStatusLabel(status: string): string {
  if (status === "auto_closed") return "auto closed";
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export interface FeedHeadline {
  lead: string;
  emphasis?: string;
  trail?: string;
}

function isTerminalStatus(status: string): boolean {
  return status === "done" || status === "auto_closed";
}

function artifactShortLabel(filename: string, businessKind: string | null): string {
  if (businessKind && ARTIFACT_LABELS[businessKind]) {
    return ARTIFACT_LABELS[businessKind];
  }
  if (businessKind) {
    return businessKind.replace(/_/g, " ");
  }
  const base = filename.replace(/\.[^.]+$/, "").replace(/_/g, " ");
  return base.length > 48 ? `${base.slice(0, 45)}…` : base;
}

export function feedHeadline(event: FeedEvent): FeedHeadline {
  if (event.kind === "task_status") {
    const client = event.entityName;
    const task = event.taskName;
    const status = event.newStatus;

    if (status === "needs_review") {
      if (client) {
        return {
          lead: `${client} —`,
          emphasis: task,
          trail: "needs your review",
        };
      }
      return { lead: task, trail: "needs your review" };
    }
    if (status === "blocked") {
      if (client) {
        return {
          lead: `${client} —`,
          emphasis: task,
          trail: "is blocked",
        };
      }
      return { lead: task, trail: "is blocked" };
    }
    if (isTerminalStatus(status)) {
      if (client) {
        return {
          lead: `${client} —`,
          emphasis: task,
          trail: "has been completed",
        };
      }
      return { lead: task, trail: "has been completed" };
    }
    if (status === "working") {
      return {
        lead: client ?? "Task",
        emphasis: task,
        trail: client ? "is in progress" : "is now working",
      };
    }
    return {
      lead: client ?? task,
      emphasis: client ? task : undefined,
      trail: `is now ${formatStatusLabel(status)}`,
    };
  }

  if (event.kind === "mission_created") {
    if (event.entityName) {
      return {
        lead: `New mission for ${event.entityName}`,
        emphasis: event.templateName ?? undefined,
      };
    }
    return { lead: "New mission", emphasis: event.missionName };
  }

  const label = artifactShortLabel(event.filename, event.businessKind);
  if (event.entityName) {
    return {
      lead: `${event.entityName}'s`,
      emphasis: label,
      trail: "is ready",
    };
  }
  return { lead: "Artefact ready", emphasis: label };
}

export function feedKindLabel(kind: FeedEvent["kind"]): string {
  switch (kind) {
    case "task_status":
      return "Task";
    case "mission_created":
      return "Mission";
    case "artifact_created":
      return "Artefact";
  }
}
